import { useDeleteInvoiceReportService } from '@/context/services/invoice_reports/InvoiceReportDestroy.service';
import { isInvalidError } from '@/context/services/type';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import { useInvalidateInvoiceReportDrawer } from '@/context/services/invoice_reports/InvoiceReportDrawer.service';
import {
  extractInvoiceReportErrorMessage,
  getStatusCodeFromError,
} from '@/features/invoice_reports/utils/extractErrorMessage';
import { useTranslation } from '@/i18n';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback } from 'react';
import { useTriggerFindTargetReport } from '../../List/context/TriggerFindTargetReportContext';

type UseDeleteInvoiceReportProps = {
  refetch: () => Promise<unknown>;
  onDrawerOpen: (id: string) => void;
  onDrawerClose: () => void;
  selectedId: string | null;
  onCloseDeleteModal: () => void;
};

const TOAST_DURATION = 5;

export const useDeleteInvoiceReport = ({
  refetch,
  onDrawerOpen,
  onDrawerClose,
  selectedId,
  onCloseDeleteModal,
}: UseDeleteInvoiceReportProps) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { mutateAsync: deleteInvoiceReport } = useDeleteInvoiceReportService();
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();
  const { onTriggerFindTargetReport, onTriggerBackToPageOneIfNoData } =
    useTriggerFindTargetReport();
  const { invalidateDrawerIfOpen } =
    useInvalidateInvoiceReportDrawer(selectedId);

  const deleteReport = useCallback(
    (reportId: string) => async () => {
      try {
        const success = await deleteInvoiceReport(reportId);
        if (!success) return;
        onCloseDeleteModal();
        notify.success({
          title: t('invoice_reports.delete_success'),
          duration: TOAST_DURATION,
        });
        await refetch();
        if (selectedId === reportId) {
          // Deleted item was open in drawer — navigate to next record
          onTriggerFindTargetReport(
            (report) => {
              onDrawerOpen(report.id);
            },
            () => {
              onDrawerClose();
            }
          );
        } else {
          onTriggerBackToPageOneIfNoData();
        }
      } catch (error) {
        const errorMessage = extractInvoiceReportErrorMessage(error, t);
        const statusCode = getStatusCodeFromError(error);

        if (isInvalidError(statusCode)) {
          await handleCheckPermission({
            fnCheckPermission: refetch,
            onCheckSuccess: () => {
              onCloseDeleteModal();
              invalidateDrawerIfOpen(reportId);
              notify.error({
                title: toHtmlNode(
                  errorMessage ?? t('invoice_reports.delete_error')
                ),
                duration: TOAST_DURATION,
              });
              onTriggerBackToPageOneIfNoData();
            },
            onCheckFailure: () => {
              location.reload();
            },
          });
          return;
        }

        await handleCheckPermission({
          fnCheckPermission: refetch,
          onCheckSuccess: () => {
            onCloseDeleteModal();
            invalidateDrawerIfOpen(reportId);
            notify.error({
              title: toHtmlNode(
                errorMessage ?? t('invoice_reports.delete_error')
              ),
              duration: TOAST_DURATION,
            });
            onTriggerBackToPageOneIfNoData();
          },
          onCheckFailure: () => {
            location.reload();
          },
        });
      }
    },
    [
      deleteInvoiceReport,
      handleCheckPermission,
      invalidateDrawerIfOpen,
      notify,
      onCloseDeleteModal,
      onDrawerOpen,
      onDrawerClose,
      refetch,
      selectedId,
      t,
      onTriggerFindTargetReport,
      onTriggerBackToPageOneIfNoData,
    ]
  );

  return {
    deleteReport,
  };
};
